// The armed state of an irreversible external action: exactly what will happen, from which
// account, to whom (new recipients highlighted), what cleanup is bundled, and the keys. What it
// shows comes from the gateway's own preview of the approval (GET …/actions/:id/preview: the same
// snapshot an approval binds, computed from the card as stored). If that preview is unavailable,
// it falls back to reading the thread (or the card's slots block) in the browser and says so.
// Confirming is always a second, separate step (Enter, the same key again, or the button); Esc
// cancels. A calendar hold or booking arms and confirms exactly like a send. Reversible mailbox
// changes (archive, labels) never arm: they run on one press with Undo (shared/actionKinds.ts).
import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Fingerprint, ShieldCheck } from "lucide-react";
import type {
  Address,
  ApprovalPreview,
  CalendarApprovalPreview,
  CardView,
  ComposeRemindDays,
} from "@shared/api";
import type { ActionKind, CardAction } from "@shared/core";
import { formatAddress } from "@web/lib/addresses";
import { cn } from "@web/lib/cn";
import { feedApi } from "@web/lib/feedApi";
import { useCardThread } from "@web/lib/feedQueries";
import { slotLabel, viewerZone, zoneNote, zoneOf } from "@web/lib/slots";
import { DEFAULT_REMIND_DAYS, remindByDefault } from "@web/compose/compose";
import { RemindToggle, type RemindChoice } from "@web/compose/RemindToggle";
import { CardSendLater, useCardSendLabel } from "@web/sendLater/CardSendLater";
import { HOLDING_ACTION_ID } from "@shared/unstick";
import { HoldingStripNote } from "./HoldingStripNote";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { actionVerb, keyedActions, sendAction } from "../actions";
import { previewRecipients } from "../recipients";

/** How long after arming a growing strip is scrolled back into view. */
const REVEAL_WINDOW_MS = 3000;

/** One labelled line of an armed action; shared with the compose sheet's strip. */
export function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-baseline gap-2 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-ink">{children}</dd>
    </div>
  );
}

/** Recipients, with those new to the thread marked; shared with the compose sheet's strip. */
export function People({ list, fresh }: { list: Address[]; fresh: ReadonlySet<string> }) {
  if (!list.length) return <span className="text-muted">nobody</span>;
  return (
    <span className="flex flex-wrap gap-x-2 gap-y-1">
      {list.map((a) => {
        const isNew = fresh.has(a.email.toLowerCase());
        return (
          <span
            key={a.email}
            className={cn(
              "inline-flex items-center gap-1",
              isNew && "rounded-md bg-note px-1.5 text-note-ink ring-1 ring-note-line",
            )}
          >
            {formatAddress(a)}
            {isNew ? <span className="text-[11px] font-semibold uppercase">new</span> : null}
          </span>
        );
      })}
    </span>
  );
}

function cleanupWords(kind: ActionKind | "gcal.release_holds" | null, count = 0): string | null {
  switch (kind) {
    case "gmail.archive_thread":
      return "Archive the thread";
    case "gmail.label":
      return "Label the thread";
    case "gcal.release_holds":
      return count === 1
        ? "Release the other held time"
        : count > 1
          ? `Release the other ${count} held times`
          : "Release the other held times";
    default:
      return null;
  }
}

const emails = (list: readonly string[]): Address[] => list.map((email) => ({ email }));

export const isCalendarAction = (action: Pick<CardAction, "actionKind">) =>
  action.actionKind === "gcal.hold" || action.actionKind === "gcal.confirm";

export function useApprovalPreview(cardId: string, actionId: string, cardRev: number) {
  return useQuery({
    // Keyed by revision: a card that changed must be previewed (and armed) again.
    queryKey: ["approvalPreview", cardId, actionId, cardRev],
    queryFn: ({ signal }) => feedApi.preview(cardId, actionId, signal),
    retry: false,
    staleTime: 0,
    gcTime: 30_000,
  });
}

/** The times a calendar action touches, labelled in the calendar's zone. */
function Times({ slots }: { slots: readonly { start: string; end: string; tz: string }[] }) {
  const note = zoneNote(zoneOf(slots), viewerZone());
  if (!slots.length) return <span className="text-muted">no times</span>;
  return (
    <span className="flex flex-col gap-0.5">
      {slots.map((s) => (
        <span key={`${s.start}/${s.end}`} className="tabular-nums" data-testid="approve-time">
          {slotLabel(s, s.tz) || `${s.start} – ${s.end}`}
        </span>
      ))}
      {note ? <span className="text-xs text-muted">{note}</span> : null}
    </span>
  );
}

/** gcal.hold / gcal.confirm rows from the gateway's preview. */
function CalendarRows({
  calendar,
  booking,
  fresh,
  cleanup,
}: {
  calendar: CalendarApprovalPreview;
  booking: boolean;
  fresh: ReadonlySet<string>;
  cleanup: ApprovalPreview["cleanup"];
}) {
  return (
    <>
      <Row label={booking ? "Time" : "Times"}>
        <Times slots={calendar.slots} />
      </Row>
      {booking ? (
        <>
          <Row label="Invite">
            <People list={emails(calendar.attendees)} fresh={fresh} />
          </Row>
          <Row label="Title">{calendar.summary}</Row>
          <Row label="Meet">{calendar.meet ? "Yes, with a Google Meet link" : "No"}</Row>
        </>
      ) : (
        <>
          <Row label="As">{calendar.summary}</Row>
          <Row label="Does">
            Puts private, tentative holds on your primary calendar. No invitations.
          </Row>
        </>
      )}
      {cleanupWords(cleanup, calendar.releaseHolds) ? (
        <Row label="Then">{cleanupWords(cleanup, calendar.releaseHolds)}</Row>
      ) : null}
    </>
  );
}

/** What the gateway says it would bind. */
function ServerRows({
  preview,
  isSend,
  action,
  accountEmail,
}: {
  preview: ApprovalPreview;
  isSend: boolean;
  action: CardAction;
  /** Shown for actions with no sender (calendar): which account it acts in. */
  accountEmail: string | null;
}) {
  const fresh = new Set(preview.newRecipients.map((e) => e.toLowerCase()));
  const from = preview.from
    ? formatAddress({ email: preview.from.address, name: preview.from.displayName ?? undefined })
    : null;
  return (
    <>
      {from ? (
        <Row label="From">{from}</Row>
      ) : accountEmail ? (
        <Row label="Account">{accountEmail}</Row>
      ) : null}
      {isSend ? (
        <>
          <Row label="To">
            <People list={emails(preview.to)} fresh={fresh} />
          </Row>
          {preview.cc.length ? (
            <Row label="Cc">
              <People list={emails(preview.cc)} fresh={fresh} />
            </Row>
          ) : null}
          {preview.bcc.length ? (
            <Row label="Bcc">
              <People list={emails(preview.bcc)} fresh={fresh} />
            </Row>
          ) : null}
          {preview.subject ? <Row label="Subject">{preview.subject}</Row> : null}
        </>
      ) : null}
      {preview.calendar ? (
        <CalendarRows
          calendar={preview.calendar}
          booking={action.actionKind === "gcal.confirm"}
          fresh={fresh}
          cleanup={preview.cleanup}
        />
      ) : null}
      {isSend && cleanupWords(preview.cleanup) ? (
        <Row label="Then">{cleanupWords(preview.cleanup)}</Row>
      ) : null}
    </>
  );
}

/** Fallback for a calendar action: the card's own slots block and the action's parameters. */
function CalendarMirrorRows({ card, action }: { card: CardView; action: CardAction }) {
  const block = card.blocks.find((b) => b.id === action.artifactBlockId);
  const all = block?.slots ?? [];
  const booking = action.actionKind === "gcal.confirm";
  const wanted = booking
    ? all.filter((s) => {
        const pick = action.params?.slot ?? (all.length === 1 ? all[0] : undefined);
        return pick ? Date.parse(pick.start) === Date.parse(s.start) : false;
      })
    : action.params?.slots
      ? all.filter((s) =>
          action.params!.slots!.some((w) => Date.parse(w.start) === Date.parse(s.start)),
        )
      : all;
  return (
    <>
      <Row label={booking ? "Time" : "Times"}>
        <Times slots={wanted} />
      </Row>
      {booking ? (
        <>
          <Row label="Invite">
            <span className="text-muted">
              {action.params?.attendeeMode === "all"
                ? "Everyone on the latest message"
                : "The person who wrote"}
              {action.params?.extraAttendees?.length
                ? ` and ${action.params.extraAttendees.join(", ")}`
                : ""}
            </span>
          </Row>
          {action.params?.eventTitle ? <Row label="Title">{action.params.eventTitle}</Row> : null}
          <Row label="Meet">{action.params?.meet ? "Yes, with a Google Meet link" : "No"}</Row>
        </>
      ) : (
        <Row label="Does">
          Puts private, tentative holds on your primary calendar. No invitations.
        </Row>
      )}
    </>
  );
}

interface ApproveStripProps {
  card: CardView;
  action: CardAction;
  defaultCleanupKind: ActionKind | null;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** A send's "remind me if they don't reply": the owner's switch (null: the default). */
  remind?: RemindChoice | null;
  onRemind?: (next: RemindChoice) => void;
  /** Told the choice that goes with the approval (undefined: the reply tracker decides). */
  onRemindDays?: (days: ComposeRemindDays | undefined) => void;
}

export function ApproveStrip({
  card,
  action,
  defaultCleanupKind,
  busy,
  onConfirm,
  onCancel,
  remind,
  onRemind,
  onRemindDays,
}: ApproveStripProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const verb = actionVerb(action);
  const isSend = action.actionKind === "gmail.send_reply" || action.actionKind === "gmail.forward";
  const isCalendar = isCalendarAction(action);
  const booking = action.actionKind === "gcal.confirm";
  const preview = useApprovalPreview(card.id, action.id, card.rev);
  const server = preview.data && preview.data.cardRev === card.rev ? preview.data : null;
  const unavailable = preview.isError;
  // Fallback only: MeeMail's own reading of the thread in the browser.
  const thread = useCardThread(isSend && unavailable ? card.id : null, card.threadKey, false);
  const mirror =
    isSend && unavailable ? previewRecipients(thread.data, card.accountEmail, action) : null;
  const fresh = server
    ? new Set(server.newRecipients.map((e) => e.toLowerCase()))
    : new Set(mirror?.newRecipients ?? []);
  // The key that armed this action confirms it when pressed again (after the double-tap guard).
  const againKey =
    sendAction(card.actions)?.id === action.id
      ? "S"
      : (keyedActions(card.actions).find((k) => k.action.id === action.id)?.key ?? null);
  const heading = isSend
    ? "Ready to send. Check who it goes to."
    : booking
      ? "Ready to book. Check the time and who is invited."
      : action.actionKind === "gcal.hold"
        ? "Ready to hold these times on your calendar."
        : `Ready: ${action.label}`;
  const undoSeconds = server?.undoSeconds ?? 0;
  // U12: "When it goes" (a send only); the confirm button says when, and waits for a time.
  const sendWhen = useCardSendLabel(card.id, isSend, action.label);
  // U5: a holding reply makes its own task (due the day it names), so no follow-up reminder.
  const holding = action.id === HOLDING_ACTION_ID;
  // On by default for a reply to one to five people, once the server says who it goes to.
  const remindShown: RemindChoice | null = holding
    ? null
    : (remind ??
      (server && isSend
        ? {
            on: remindByDefault({
              threadId: action.actionKind === "gmail.send_reply" ? card.threadKey : null,
              to: server.to,
              cc: server.cc,
              bcc: server.bcc,
            }),
            days: DEFAULT_REMIND_DAYS,
          }
        : null));
  const effectiveDays: ComposeRemindDays | undefined = remindShown
    ? remindShown.on
      ? remindShown.days
      : 0
    : undefined;
  useEffect(() => {
    onRemindDays?.(effectiveDays);
  }, [onRemindDays, effectiveDays]);

  // What is being confirmed must be on screen: reveal the strip, then focus Confirm. The server's
  // preview arrives a moment after arming and makes the strip taller, so it is revealed again
  // when it grows (briefly: never a fight with a reader who scrolls). scroll-mb keeps it clear of
  // the Dock below the pane.
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    confirmRef.current?.focus({ preventScroll: true });
    const el = stripRef.current;
    if (!el) return;
    el.scrollIntoView({ block: "nearest" });
    if (typeof ResizeObserver === "undefined") return;
    let height = el.getBoundingClientRect().height;
    const observer = new ResizeObserver(() => {
      const next = el.getBoundingClientRect().height;
      if (next > height + 1) el.scrollIntoView({ block: "nearest" });
      height = next;
    });
    observer.observe(el);
    const stop = setTimeout(() => observer.disconnect(), REVEAL_WINDOW_MS);
    return () => {
      clearTimeout(stop);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={stripRef}
      role="group"
      aria-label={`Confirm: ${action.label}`}
      className="flex scroll-mb-6 flex-col gap-3 rounded-xl border border-accent/40 bg-accent-soft/60 px-4 py-3"
    >
      <p className="flex items-center gap-2 font-medium text-ink">
        <ShieldCheck aria-hidden className="size-4 text-accent-text" />
        {heading}
      </p>
      <dl className="flex flex-col gap-1.5" aria-busy={preview.isPending || undefined}>
        {server ? (
          <ServerRows
            preview={server}
            isSend={isSend}
            action={action}
            accountEmail={card.accountEmail}
          />
        ) : unavailable ? (
          <>
            {card.accountEmail ? (
              <Row label={isSend ? "From" : "Account"}>{card.accountEmail}</Row>
            ) : null}
            {mirror ? (
              <>
                <Row label="To">
                  {thread.isPending ? (
                    <span className="text-muted">Reading the thread…</span>
                  ) : (
                    <People list={mirror.to} fresh={fresh} />
                  )}
                </Row>
                {mirror.cc.length ? (
                  <Row label="Cc">
                    <People list={mirror.cc} fresh={fresh} />
                  </Row>
                ) : null}
              </>
            ) : null}
            {isCalendar ? <CalendarMirrorRows card={card} action={action} /> : null}
            {isSend && cleanupWords(defaultCleanupKind) ? (
              <Row label="Then">{cleanupWords(defaultCleanupKind)}</Row>
            ) : null}
          </>
        ) : (
          <>
            {card.accountEmail ? (
              <Row label={isSend ? "From" : "Account"}>{card.accountEmail}</Row>
            ) : null}
            {isSend ? (
              <Row label="To">
                <span className="text-muted">Checking who it goes to…</span>
              </Row>
            ) : null}
            {isCalendar ? (
              <Row label={booking ? "Time" : "Times"}>
                <span className="text-muted">Checking your calendar…</span>
              </Row>
            ) : null}
          </>
        )}
      </dl>
      {fresh.size ? (
        <p className="text-sm text-note-ink">
          {fresh.size === 1 ? "One person is" : `${fresh.size} people are`} new to this thread.
        </p>
      ) : null}
      {holding ? <HoldingStripNote cardId={card.id} /> : null}
      {isSend && onRemind && !holding ? (
        <RemindToggle
          value={remindShown ?? { on: false, days: DEFAULT_REMIND_DAYS }}
          onChange={onRemind}
          disabled={busy}
        />
      ) : null}
      {isSend ? (
        <CardSendLater
          cardId={card.id}
          disabled={busy}
          reply={action.actionKind === "gmail.send_reply"}
        />
      ) : null}
      {unavailable ? (
        <p className="text-sm text-muted" role="status">
          {isCalendar
            ? "The server’s preview is unavailable, so this is MeeMail’s own reading of the card. The server still checks the times against your rules and your calendar before anything happens."
            : "The server’s preview is unavailable, so this is MeeMail’s own reading of the thread. The server still checks the exact text and recipients before anything happens."}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">
          <Kbd>Enter</Kbd> to confirm
          {againKey ? (
            <>
              {" "}
              (or <Kbd>{againKey}</Kbd> again)
            </>
          ) : null}{" "}
          · <Kbd>Esc</Kbd> to cancel
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            ref={confirmRef}
            size="sm"
            variant="primary"
            busy={busy}
            disabled={!!sendWhen.problem}
            // A held Enter must not confirm: ignore auto-repeated key presses on this button.
            onKeyDown={(e) => {
              if (e.repeat) e.preventDefault();
            }}
            onClick={onConfirm}
            data-testid="approve-confirm"
          >
            {busy ? `${verb.doing}…` : sendWhen.label}
          </Button>
        </div>
      </div>
      {server ? (
        <div className="flex flex-col gap-0.5 text-xs text-muted">
          <p
            className="inline-flex items-center gap-1"
            title="Your approval is bound to exactly this and checked again right before anything happens."
          >
            <Fingerprint aria-hidden className="size-3.5" />
            <span>Fingerprint</span>
            <code className="font-mono text-ink-2" data-testid="approve-fingerprint">
              {server.fingerprint}
            </code>
          </p>
          <p>
            {isSend
              ? `MeeMail checks this exact text and these recipients again right before it sends.${
                  undoSeconds > 0
                    ? ` You can undo for ${undoSeconds} seconds after confirming.`
                    : ""
                }`
              : booking
                ? `MeeMail checks your rules and your calendar again right before it books; Google then emails the invitation.${
                    undoSeconds > 0
                      ? ` You can undo for ${undoSeconds} seconds after confirming.`
                      : ""
                  }`
                : isCalendar
                  ? "MeeMail checks your rules and your calendar again right before it holds these. Holds are private and easy to release."
                  : "MeeMail checks this again right before it runs."}
          </p>
        </div>
      ) : isSend ? (
        <p className="text-xs text-muted">
          MeeMail checks the exact text and recipients again right before it sends. You can undo for
          a few seconds after confirming.
        </p>
      ) : isCalendar ? (
        <p className="text-xs text-muted">
          MeeMail checks your rules and your calendar again right before anything is written.
          {booking ? " You can undo for a few seconds after confirming." : ""}
        </p>
      ) : null}
    </div>
  );
}
